<template>
  <div class="chatbot-container">
    <div v-if="!isExpanded" class="chat-button" @click="toggleChat" role="button" aria-label="Open chat" tabindex="0"
      @keydown.enter="toggleChat" @keydown.space="toggleChat">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="chat-icon">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
      </svg>
      <span>{{ buttonText }}</span>
    </div>

    <div v-show="isExpanded" class="chat-modal-overlay" @click.self="closeChat">

      <div class="chat-modal" :class="{ 'keyboard-visible': isKeyboardVisible && isMobile() }" role="dialog" aria-labelledby="chatModalTitle" aria-describedby="chatModalDescription">
        <div class="chat-modal-header">
          <button @click.stop="closeChat" class="chat-back-btn" aria-label="Close chat">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
          </button>
          <h3 id="chatModalTitle" class="chat-modal-title">{{ chatbotTitle }}</h3>
          <div class="header-actions">
            <button v-if="messages.length > 0" @click.stop="resetSession" class="chatbot-reset-btn" aria-label="Start new chat">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="m3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              <span>New Chat</span>
            </button>
            <button @click.stop="closeChat" class="chat-close-btn" aria-label="Close chat">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        <!-- Reset Confirmation Banner -->
        <div v-if="showResetConfirmation" class="reset-confirmation-banner">
          <div class="reset-warning-content">
            <div class="reset-warning-icon">⚠️</div>
            <div class="reset-warning-text">
              <div class="reset-warning-title">Start a new conversation?</div>
              <div class="reset-warning-subtitle">Your current chat will be lost.</div>
            </div>
          </div>
          <div class="reset-warning-actions">
            <button @click="cancelReset" class="reset-cancel-btn">Cancel</button>
            <button @click="confirmReset" class="reset-confirm-btn">Start New Chat</button>
          </div>
        </div>

        <div id="chatModalDescription" class="chat-messages" :class="chatMessagesClass" ref="messageContainer">
          <!-- Quick Actions Empty State -->
          <div v-if="messages.length === 0 && !isAiLoading" class="empty-state">
            <QuickActions 
              :chat-agent-id="chatAgentId"
              :attendee-id="attendeeId"
              :display-config="chatAgent?.display_config"
              @action-selected="handleQuickAction"
            />
          </div>
          <div v-for="message in messages" :key="message.id"
              :class="['message', message.sender]"
              :data-message-id="message.id"> 
              <div class="message-content">
                  <!-- Structured response handling -->
                  <div v-if="message.sender === 'ai' && message.structuredResponse">
                    <component
                      :is="getResponseComponent(message.structuredResponse.response_type)"
                      :response="message.structuredResponse"
                      @action-click="handleResponseAction"
                      @attendee-action="handleAttendeeAction"
                      @session-action="handleSessionAction"
                      @session-click="handleSessionClick"
                      @speaker-click="handleSpeakerClick"
                      @tag-click="handleTagClick"
                    />
                  </div>
                  
                  <!-- Fallback to regular text formatting -->
                  <div class='general-text-message' v-else v-html="formatMessage(message.text)"></div>
                  
                  <div v-if="message.sender === 'ai' && message.isLoading" class="ai-typing-indicator ai-loading-indicator">
                    <div class="loading-blocks-svg-container">
                      <img src="/images/loadingBlocks.svg" class="loading-blocks-svg" aria-label="AI is typing indicator" />
                    </div>
                    <div class="'ai-typing-text">Just a sec</div>
                      <!-- <span>.</span><span>.</span><span>.</span> -->
                  </div>
                  
                  <span class="message-timestamp">{{ message.timestamp }}</span>
              </div>
          </div>

          <div v-if="isAiLoading && messages.length > 0 && messages[messages.length - 1].sender !== 'ai'"
              class="message ai loading"> <div class="message-content">
                <p><span class="ai-typing-indicator">.</span><span class="ai-typing-indicator">.</span><span class="ai-typing-indicator">.</span></p>
              </div>
          </div>
        </div>

        <div class="chat-input-area">
          <div class="input-wrapper">
            <textarea 
              rows="1"
              v-model="newMessage" 
              @keyup.enter.exact="handleSendMessage" 
              @input="autoResize"
              placeholder="Type your message" 
              aria-label="Type your message" 
              ref="messageInput" 
              :disabled="isAiLoading" 
              @focus="onInputFocus" 
              @blur="onInputBlur"
            ></textarea>
            <button @click="handleSendMessage" :disabled="isAiLoading || !newMessage.trim()" 
                    class="send-button" aria-label="Send message">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" 
                   fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22,2 15,22 11,13 2,9"></polygon>
              </svg>
            </button>
          </div>
        </div>
        
      </div>
    </div>
  </div>
</template>

<script>
// Helper to generate simple UUIDs for session IDs
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

 import { marked } from 'marked';
 import DOMPurify from 'dompurify';

 
 // Import response components
 import GeneralChatResponse from './GeneralChatResponse.vue';
 import AttendeeSearchResults from './AttendeeSearchResults.vue';
 import ScheduleSearchResults from './ScheduleSearchResults.vue';
 import QuickActions from './QuickActions.vue';
 
 // Configure marked options for security
 marked.setOptions({
   breaks: true, // Convert \n to <br>
   gfm: true, // GitHub Flavored Markdown
   headerIds: false, // Disable header IDs for security
   mangle: false, // Don't mangle email addresses
 });
 

export default {
  name: 'ChatbotComponent',
  components: {
    GeneralChatResponse,
    AttendeeSearchResults,
    ScheduleSearchResults,
    QuickActions,
  },
  props: {
    eventId: {
      type: [String, Number],
      required: false,
      default: 1
    },
    chatAgent: {
      type: Object,
      required: true
    },
    chatAgentId: {
      type: [String, Number],
      required: true
    },
    attendeeId: {
      type: [String, Number],
      required: false,
      default: null
    },
    // Optional: if you have CSRF tokens in a meta tag
    csrfToken: {
      type: String,
      default: () => {
        const tokenMeta = document.querySelector('meta[name="csrf-token"]');
        return tokenMeta ? tokenMeta.getAttribute('content') : '';
      }
    },
    chatApiUrl: {
      type: String,
      default: '/chat/stream' // Default API endpoint
    }
  },
  data() {
    return {
      isExpanded: false,
      newMessage: '',
      messages: [],
      messageIdCounter: 0,
      sessionId: null,
      isAiLoading: false, // Indicates if AI is currently "typing" or processing
      showResetConfirmation: false, // Shows the inline reset confirmation banner
      isKeyboardVisible: false, // Track if virtual keyboard is visible on mobile
      chatbotTitle: this.chatAgent.name || 'Eventfinity Assistant', // Title for the chatbot
      // reader: null, // To store the stream reader for potential cancellation
    };
  },
  watch: {
    // Watch messages array for changes (no auto-scroll - let user control reading)
    messages: {
      handler() {
        // No automatic scrolling - user controls scroll position
        // Scrolling is handled manually when user sends messages
      },
      deep: true // Also watch for changes within message objects (e.g., text updates)
    },
    isExpanded(newVal) {
      if (newVal) {
        // When modal opens, prevent body from scrolling
        document.body.style.setProperty('overflow', 'hidden', 'important');
        if (!this.sessionId) {
          this.sessionId = generateUUID();
        }
        this.$nextTick(() => {
          if (this.$refs.messageInput) {
            this.$refs.messageInput.focus();
          }
          this.scrollToBottom();
        });
      } else {
        // When modal closes, restore body scrolling
        document.body.style.removeProperty('overflow');

      }
    }
  },
  computed: {
    /**
     * Get the button text from display_config or fallback to default
     */
    buttonText() {
      return this.chatAgent?.display_config?.button_text || 'Chat with us!';
    },
    
    /**
     * Determine the appropriate CSS class for chat messages container
     * based on current chat state
     */
    chatMessagesClass() {
      if (this.messages.length === 0) {
        // Empty state - minimal padding for QuickActions visibility
        return 'chat-messages-empty';
      }
      
      // Check if the last message was sent by user and if AI is currently loading
      // This indicates a fresh user message that should get scroll-to-top treatment
      const lastMessage = this.messages[this.messages.length - 1];
      const userJustSentMessage = lastMessage && lastMessage.sender === 'user' && this.isAiLoading;
      
      if (userJustSentMessage) {
        // Fresh conversation state - large padding for scroll-to-top effect
        return 'chat-messages-fresh';
      }
      
      // Normal conversation state - moderate padding
      return 'chat-messages-with-content';
    }
  },
  methods: {
    formatMessage(text) {
      // Basic security: escape HTML, then replace newlines with <br>
      // If your AI can return Markdown, you'd use a Markdown library here.
      // For now, simple text formatting.
      if (typeof text !== 'string') return '';
        
        // Parse markdown to HTML
        const rawHtml = marked.parse(text);
        
        // Sanitize HTML to prevent XSS attacks
        const cleanHtml = DOMPurify.sanitize(rawHtml, {
          ALLOWED_TAGS: [
            'p', 'br', 'strong', 'em', 'u', 's', 'code', 'pre',
            'blockquote', 'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3',
            'h4', 'h5', 'h6', 'hr', 'table', 'thead', 'tbody', 'tr',
            'th', 'td', 'img', 'b', 'i', 'span'
          ],
          ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'target', 'rel', 'class'],
          ALLOW_DATA_ATTR: false,
          USE_PROFILES: { html: true }
        });
        
        return cleanHtml;
    },

    /**
     * Detect if a response is structured and parse it
     */
    parseStructuredResponse(text) {
      if (typeof text !== 'string') return null;
      
      // Try to detect JSON response
      const trimmed = text.trim();
      if (trimmed.startsWith('{')) {
        try {
          // First try parsing as-is
          if (trimmed.endsWith('}')) {
            const parsed = JSON.parse(trimmed);
            if (parsed.response_type && parsed.status) {
              return parsed;
            }
          } else {
            // JSON might be truncated, try to recover
            console.warn('Detected truncated JSON response, attempting recovery...');
            
            // Try to find the last complete object by finding nested closing braces
            let lastValidJson = trimmed;
            let braceCount = 0;
            let lastValidIndex = -1;
            
            for (let i = 0; i < trimmed.length; i++) {
              if (trimmed[i] === '{') braceCount++;
              if (trimmed[i] === '}') {
                braceCount--;
                if (braceCount === 0) {
                  lastValidIndex = i;
                }
              }
            }
            
            if (lastValidIndex > 0) {
              lastValidJson = trimmed.substring(0, lastValidIndex + 1);
              try {
                const parsed = JSON.parse(lastValidJson);
                if (parsed.response_type && parsed.status) {
                  console.warn('Successfully recovered truncated JSON response');
                  return parsed;
                }
              } catch (recoveryError) {
                console.error('Failed to recover truncated JSON:', recoveryError);
              }
            }
          }
        } catch (e) {
          // Not valid JSON or not a structured response
          console.error('Failed to parse structured response:', e);
          console.error('Response text:', trimmed.substring(0, 500) + '...');
          
          // If it looks like it should be structured but parsing failed, 
          // create an error response to inform the user
          if (trimmed.includes('"response_type"') && trimmed.includes('"status"')) {
            return {
              response_type: 'general_chat',
              status: 'error',
              content: {
                message: 'I received a response that was too large and got cut off. Please try asking for fewer results or a more specific query.',
                actions: [
                  { id: 'retry', text: 'Try Again', action_type: 'quick_fill', quick_fill: 'Please try that again with fewer results' }
                ]
              }
            };
          }
        }
      }
      return null;
    },

    /**
     * Get the appropriate component for a response type
     */
    getResponseComponent(responseType) {
      const componentMap = {
        'general_chat': 'GeneralChatResponse',
        'attendee_search_results': 'AttendeeSearchResults',
        'schedule_search_results': 'ScheduleSearchResults'
      };
      return componentMap[responseType] || 'GeneralChatResponse';
    },

    /**
     * Handle action clicks from response components
     */
    handleResponseAction(action) {
      console.log('Response action clicked:', action);
      
      // If action has quick_fill, send it as a message
      if (action.quick_fill) {
        this.newMessage = action.quick_fill;
        this.handleSendMessage();
      }
    },

    /**
     * Handle attendee-specific actions
     */
    handleAttendeeAction(actionData) {
      console.log('Attendee action:', actionData);
      
      const { action, attendee } = actionData;
      
      switch (action) {
        case 'view_profile':
          // Open profile in new window/modal or send a chat message
          this.newMessage = `Show me ${attendee.name}'s profile`;
          this.handleSendMessage();
          break;
        case 'send_message':
          // Navigate to messaging system or open chat
          window.open(`/attendees/${attendee.id}/message`, '_blank');
          break;
        case 'add_connection':
          // Send connection request
          this.newMessage = `Connect with ${attendee.name}`;
          this.handleSendMessage();
          break;
        case 'view_sessions':
          // Show attendee's sessions
          this.newMessage = `Show me ${attendee.name}'s sessions`;
          this.handleSendMessage();
          break;
        case 'schedule_meeting':
          // Open scheduling interface
          this.newMessage = `Schedule a meeting with ${attendee.name}`;
          this.handleSendMessage();
          break;
        default:
          console.log('Unknown attendee action:', action);
      }
    },

    /**
     * Handle session-specific actions
     */
    handleSessionAction(actionData) {
      console.log('Session action:', actionData);
      
      const { action, session } = actionData;
      debugger      
      switch (action) {
        case 'add_to_schedule':
          // Add session to personal schedule
          this.newMessage = `Add "${session.title}" to my schedule`;
          this.handleSendMessage();
          break;
        case 'join_session':
          // Join/navigate to session
          if (session.join_url) {
            window.open(session.join_url, '_blank');
          } else {
            this.newMessage = `How do I join "${session.title}"?`;
            this.handleSendMessage();
          }
          break;
        case 'view_details':
          // Request more session details
          
          var message = session.date ?
            `Tell me more about session "${session.title}" on ${session.date}` :
            `Tell me more about session "${session.title}"`;
          this.newMessage = message;
          this.handleSendMessage();
          break;
        case 'share_session':
          // Share session
          if (navigator.share) {
            navigator.share({
              title: session.title,
              text: `Check out this session: ${session.title} at ${session.start_time}`,
              url: window.location.href
            });
          } else {
            this.newMessage = `Share "${session.title}" with others`;
            this.handleSendMessage();
          }
          break;
        case 'set_reminder':
          // Set reminder for session
          this.newMessage = `Remind me about "${session.title}"`;
          this.handleSendMessage();
          break;
        default:
          console.log('Unknown session action:', action);
      }
    },

    /**
     * Handle session clicks
     */
    handleSessionClick(sessionData) {
      console.log('Session clicked:', sessionData);
      
      let session;
      
      // Handle both direct session objects and nested session data
      if (sessionData.session) {
        session = sessionData.session;
      } else if (sessionData.title) {
        session = sessionData;
      } else {
        console.warn('Invalid session data:', sessionData);
        return;
      }
      // Send a message asking for more details about this session
      this.newMessage = `Tell me more about "${session.name}" at ${session.start_time}`;
      this.handleSendMessage();
    },

    /**
     * Handle speaker clicks from session cards
     */
    handleSpeakerClick(speakerData) {
      console.log('Speaker clicked:', speakerData);
      
      const { speaker, session } = speakerData;
      
      // Request more information about the speaker
      this.newMessage = `Tell me about speaker ${speaker.name}`;
      this.handleSendMessage();
    },

    /**
     * Handle tag clicks from session cards
     */
    handleTagClick(tagData) {
      console.log('Tag clicked:', tagData);
      
      const { tag, session } = tagData;
      
      // Search for other sessions with the same tag
      this.newMessage = `Show me other sessions about ${tag}`;
      this.handleSendMessage();
    },
    
    /**
     * Handle quick action selections from QuickActions component
     */
    handleQuickAction(query) {
      console.log('Quick action selected:', query);
      
      // Set the query as the new message and send it
      this.newMessage = query;
      this.handleSendMessage();
    },

    /**
     * Reset the chat session to start a new conversation
     */
    resetSession() {
      // Show confirmation banner if there are messages
      if (this.messages.length > 0) {
        this.showResetConfirmation = true;
        return;
      }
      
      // If no messages, reset immediately
      this.performReset();
    },

    /**
     * Cancel the reset operation
     */
    cancelReset() {
      this.showResetConfirmation = false;
    },

    /**
     * Confirm and perform the reset operation
     */
    confirmReset() {
      this.showResetConfirmation = false;
      this.performReset();
    },

    /**
     * Perform the actual reset operation
     */
    performReset() {
      console.log('Resetting chat session');
      
      // Clear all session data
      this.messages = [];
      this.messageIdCounter = 0;
      this.sessionId = generateUUID(); // Generate new session ID
      this.isAiLoading = false;
      this.newMessage = '';
      
      // Reset to top of chat container and focus input
      this.$nextTick(() => {
        if (this.$refs.messageInput) {
          this.$refs.messageInput.focus();
        }
        this.autoResize(); // Reset textarea height
        this.scrollToTop();
      });
      
      console.log('Chat session reset complete. New session ID:', this.sessionId);
    },
    
    toggleChat() {
      this.isExpanded = !this.isExpanded;
    },
    closeChat() {
      this.isExpanded = false;
      this.$forceUpdate(); // Force Vue to re-render
      // Optional: if you want to cancel an ongoing stream when chat is closed
      // if (this.reader) {
      //   this.reader.cancel('Chat closed by user').catch(e => console.warn('Error cancelling stream reader:', e));
      //   this.reader = null;
      //   this.isAiLoading = false;
      //   // Remove or update any partial AI message
      //   const lastMessage = this.messages[this.messages.length - 1];
      //   if (lastMessage && lastMessage.sender === 'ai' && lastMessage.isLoading) {
      //       lastMessage.text = lastMessage.text ? lastMessage.text + ' (Cancelled)' : '(Cancelled)';
      //       lastMessage.isLoading = false;
      //   }
      // }
    },
    getConversationHistory() {
      // Prepare history for the backend, excluding any current loading AI message
      return this.messages
        .filter(msg => !(msg.sender === 'ai' && msg.isLoading)) // Exclude placeholder loading messages
        .map(msg => ({
          role: msg.sender === 'ai' ? 'model' : 'user',
          text: msg.text,
        }));
    },
    // In Chatbot.vue methods:
    async handleSendMessage() {
      const trimmedMessage = this.newMessage.trim();
      if (!trimmedMessage || this.isAiLoading) {
        return;
      }

      this.messageIdCounter++;
      const userMessageId = this.messageIdCounter;
      this.messages.push({
        id: userMessageId,
        text: trimmedMessage,
        sender: 'user',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isLoading: false,
      });
      const userMessageText = trimmedMessage;
      this.newMessage = ''; // Clear input after preparing the message
      
      // Reset textarea height after clearing and scroll to user message
      this.$nextTick(() => {
        this.autoResize();
        this.scrollToUserMessage(userMessageId);
      });

      // Add placeholder for AI response
      this.messageIdCounter++;
      const aiMessageId = this.messageIdCounter;
      this.messages.push({
        id: aiMessageId,
        text: '', // Initially empty
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isLoading: true, // AI is "typing"
      });
      this.isAiLoading = true; // Global loading state

      // Find the AI message object to update it later
      const currentAiMessage = this.messages.find(m => m.id === aiMessageId);

      const payload = {
        message: userMessageText,
        event_id: this.eventId,
        // Send history *before* adding the current user message and AI placeholder
        history: this.getConversationHistory().filter(m => m.id !== aiMessageId && m.text !== trimmedMessage),
        session_id: this.sessionId,
        chat_agent_id: this.chatAgentId,
      };
      try {
        // --- MODIFIED FETCH CALL ---
        const response = await fetch(this.chatApiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json', // Expecting a JSON response
            'X-CSRF-TOKEN': this.csrfToken,
          },
          body: JSON.stringify(payload),
        });

        // --- PROCESS JSON RESPONSE ---
        const responseData = await response.json(); // Always try to parse JSON

        if (!response.ok) {
          // Server responded with an error status (4xx, 5xx)
          console.error('Error from server:', response.status, responseData);
          if (currentAiMessage) {
            currentAiMessage.text = `Error: ${responseData.error || responseData.message || 'Could not connect to the assistant.'}`;
          }
          // The finally block will handle isLoading states
          return; // Exit early
        }

        // Success - response.ok is true
        if (currentAiMessage) {
          const responseText = responseData.text || 'I seem to be at a loss for words.';
          currentAiMessage.text = responseText;
          
          // Try to parse structured response
          const structuredResponse = this.parseStructuredResponse(responseText);
          if (structuredResponse) {
            currentAiMessage.structuredResponse = structuredResponse;
            console.log('Parsed structured response:', structuredResponse);
          }
          
          if (responseData.session_id && this.sessionId !== responseData.session_id) {
            this.sessionId = responseData.session_id; // Update session ID if backend sent a new one
          }
        }

      } catch (error) {
        // Network error or other issues with fetch itself, or if .json() fails on non-JSON error response
        console.error('Failed to send message or parse response:', error);
        if (currentAiMessage) {
          // Avoid overwriting a more specific error if already set
          if (!currentAiMessage.text || !currentAiMessage.text.toLowerCase().includes('error')) {
             currentAiMessage.text = 'Error: Connection failed or the response was unreadable.';
          }
        }
      } finally {
        if (currentAiMessage) {
          currentAiMessage.isLoading = false; // AI finished "typing"
        }
        this.isAiLoading = false; // Reset global loading state
        // No auto-scroll here - let user control scrolling through AI responses
      }
    },


    scrollToBottom() {
      const container = this.$refs.messageContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },

    /**
     * Scroll to a specific user message smoothly - positions at top for fresh conversation feel
     */
    scrollToUserMessage(messageId) {
      this.$nextTick(() => {
        const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
        const container = this.$refs.messageContainer;
        
        if (messageElement && container) {
          // Calculate position to place message at top of visible area
          const targetPosition = messageElement.offsetTop - 20; // 20px offset from top
          
          // Use smooth scrolling to position message at top
          container.scrollTo({
            top: Math.max(0, targetPosition), // Ensure we don't scroll above content
            behavior: 'smooth'
          });
        }
      });
    },

    scrollToTop() {
      const container = this.$refs.messageContainer;
      if (container) {
        container.scrollTop = 0;
      }
    },

    /**
     * Handle mobile virtual keyboard focus
     */
    onInputFocus() {
      this.isKeyboardVisible = true;
      // On mobile, scroll to input when keyboard appears
      // if (this.isMobile()) {
      //   setTimeout(() => {
      //     if (this.$refs.messageInput) {
      //       this.$refs.messageInput.scrollIntoView({ 
      //         behavior: 'smooth', 
      //         block: 'nearest' 
      //       });
      //     }
      //   }, 300); // Small delay for keyboard animation
      // }
    },

    /**
     * Handle mobile virtual keyboard blur
     */
    onInputBlur() {
      this.isKeyboardVisible = false;
    },

    /**
     * Auto-resize textarea based on content
     */
    autoResize() {
      const textarea = this.$refs.messageInput;
      if (textarea) {
        // Reset height to auto to get the correct scrollHeight
        textarea.style.height = 'auto';
        
        // Calculate the new height based on content
        const scrollHeight = textarea.scrollHeight;
        const maxHeight = 120; // Approximately 5 lines
        const minHeight = 40;  // Single line height
        
        // Set height to content size, but within limits
        if (scrollHeight <= maxHeight) {
          textarea.style.height = Math.max(scrollHeight, minHeight) + 'px';
        } else {
          textarea.style.height = maxHeight + 'px';
        }
      }
    },

    /**
     * Check if device is mobile
     */
    isMobile() {
      return window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }
  },

  beforeUnmount() {
    // Clean up if needed, e.g., if you had a global EventSource object
    // if (this.reader) {
    //    this.reader.cancel('Component unmounting').catch(e => console.warn('Error cancelling stream reader:', e));
    // }
  }
};
</script>

<style>
.message.user .message-content .general-text-message p {
  color: var(--theme-primary-text-color, #ffffff) !important;
}

</style>

<style scoped>
/* General container for the chatbot elements */

.chatbot-container {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  line-height: 1.6;
}


/* Minimized chat button styling */

.chat-button {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #007bff;
  color: white;
  padding: 12px 20px;
  border-radius: 25px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
  z-index: 999;
}

.chat-button:hover {
  background-color: #0056b3;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.chat-button .chat-icon {
  margin-right: 8px;
}
/* Expanded chat modal styling */
.chat-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  /* Semi-transparent overlay */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 10px;
  /* Padding for smaller screens */
}

.chat-modal {
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* Ensures children conform to border-radius */
  /* Sizing - takes up most of the screen */
  width: 90vw;
  height: 85vh;
  max-width: 700px;
  /* Max width for larger screens */
  max-height: 900px;
  /* Max height */
  -webkit-transform: translate3d(0,0,0); /* Force hardware acceleration and a new stacking context */
}
/* Modal Header */

.chat-modal-header {
  display: flex;  
  justify-content: space-between;
  align-items: center;
    /* Modify the padding to account for the top safe area */
  padding: calc(16px + env(safe-area-inset-top)) 20px 16px;
  background-color: #ffffff;
  border-bottom: 1px solid #f0f0f0;
}

.chat-modal-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 500;
  color: #1a1a1a;
  flex: 1;
  text-align: left;
}



.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.chatbot-reset-btn {
  background: var(--theme-primary-bg-color);
  border: 1px solid var(--theme-content-bg-text-color-faded);
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  color: var(--theme-content-bg-text-color);
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
  line-height: 1;
  text-decoration: none;
  box-shadow: none;
  font-family: inherit;
  display: flex;
  justify-content: center;
}

.chatbot-reset-btn:hover {
  background-color: var(--theme-primary-bg-color);
  border-color: var(--theme-primary-color);
  color: var(--theme-navigation-active-text-color);
  text-decoration: none;
}

.chatbot-reset-btn:focus {
  outline: 2px solid var(--theme-primary-color);
  outline-offset: 1px;
}

.chatbot-reset-btn svg {
  flex-shrink: 0;
}

.chat-back-btn,
.chat-close-btn {
  background: var(--theme-primary-color, #0F2FEB);
  border: none;
  cursor: pointer;
  padding: 8px;
  line-height: 1;
  text-decoration: none;
  box-shadow: none;
  font-family: inherit;
  border-radius: 50%;
  transition: all 0.2s ease;
  color: var(--theme-primary-text-color, #FFFFFF);
  display: flex;
  align-items: center;
  justify-content: center;
}

.chat-back-btn:hover,
.chat-close-btn:hover {
  background-color: var(--theme-secondary-color, #03187E);
  color: var(--theme-primary-text-color, #FFFFFF);
}

.chat-back-btn:focus,
.chat-close-btn:focus {
  outline: 2px solid var(--theme-primary-color);
  outline-offset: 1px;
}

.chat-back-btn svg,
.chat-close-btn svg {
  display: block;
}

/* Hide back button on all devices - use X button consistently */
.chat-back-btn {
  display: none;
}

/* Reset Confirmation Banner */
.reset-confirmation-banner {
  background: var(--theme-event-bg-color);
  border-bottom: 1px solid var(--theme-content-bg-text-color-faded);
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.reset-warning-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.reset-warning-icon {
  font-size: 1.2rem;
  line-height: 1;
}

.reset-warning-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.reset-warning-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--theme-event-bg-text-color);
  line-height: 1.2;
}

.reset-warning-subtitle {
  font-size: 0.8rem;
  color: var(--theme-content-bg-text-color);
  opacity: 0.7;
  line-height: 1.2;
}

.reset-warning-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.reset-cancel-btn {
  background: var(--theme-content-bg-color);
  border: 1px solid var(--theme-content-bg-text-color-faded);
  border-radius: 5px;
  padding: 6px 12px;
  cursor: pointer;
  color: var(--theme-content-bg-text-color);
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s ease;
  line-height: 1;
  text-decoration: none;
  font-family: inherit;
}

.reset-cancel-btn:hover {
  background-color: var(--theme-content-bg-text-color-extra-faded);
  border-color: var(--theme-content-bg-text-color);
}

.reset-confirm-btn {
  background: var(--theme-primary-color);
  border: 1px solid var(--theme-primary-color);
  border-radius: 5px;
  padding: 6px 12px;
  cursor: pointer;
  color: var(--theme-primary-text-color);
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s ease;
  line-height: 1;
  text-decoration: none;
  font-family: inherit;
}

.reset-confirm-btn:hover {
  background-color: var(--theme-primary-bg-color);
  border-color: var(--theme-primary-color);
}

.reset-cancel-btn:focus,
.reset-confirm-btn:focus {
  outline: 2px solid var(--theme-primary-color);
  outline-offset: 1px;
}

/* Message Display Area */
.chat-messages {
  flex-grow: 1;
  /* Takes available space */
  overflow-y: auto;
  /* Enables scrolling for messages */
  padding: 20px;
  background-color: #ffffff;
  /* Message area background */
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
}

/* Dynamic padding classes for different chat states */
.chat-messages-empty {
  padding-bottom: 20px; /* Minimal padding for QuickActions visibility */
}

.chat-messages-with-content {
  padding-bottom: 30vh; /* Moderate padding for normal conversation */
}

.chat-messages-fresh {
  padding-bottom: 50vh; /* Large padding for fresh conversation scroll-to-top effect */
}

.no-messages {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #6c757d;
  text-align: center;
}

/* Empty State with Quick Actions */
.empty-state {
  height: 100%;
  overflow-y: auto;
  padding: 0;
}

/* Individual Message Styling */
.message {
  margin-bottom: 15px;
  display: flex;
  max-width: 80%;
  /* Max width of a message bubble */
}

.message-content {
  padding: 10px 15px;
  border-radius: 18px;
  word-wrap: break-word;
  /* Break long words */
}

.message p {
  margin: 0 0 5px 0;
  /* Space between text and timestamp */
  font-size: 0.95rem;
}

.message-timestamp {
  font-size: 0.75rem;
  color: #888;
  display: block;
  /* Puts timestamp on new line or control with flex */
}
/* User's messages */
.message.user {
  justify-content: flex-end;
  /* Align to the right */
  margin-left: auto;
  /* Push to the right */
}

.message.user .message-content {
  background-color: var(--primary-color, #007bff);
  /* User message color */
  color: white;
}

.message.user .message-timestamp {
  color: #e0e0e0;
  text-align: right;
}
/* AI's messages */
.message.ai {
  justify-content: flex-start;
  /* Align to the left */
  margin-right: auto;
  /* Push to the left */
}

.message.ai .message-content {
  background-color: #e9ecef;
  /* AI message color */
  color: #333;
}

.message.ai .message-timestamp {
  color: #6c757d;
  text-align: left;
}
/* Message Input Area */
.chat-input-area {
  padding: 16px;
  background-color: #ffffff;
  border-top: 1px solid #f0f0f0;
}

.input-wrapper {
  display: flex;
  align-items: flex-end;
  border: 1px solid #e5e5e5;
  border-radius: 24px;
  padding: 8px 16px;
  background: #fafafa;
  transition: all 0.2s ease;
}

.input-wrapper:focus-within {
  border-color: #d0d0d0;
  background: #ffffff;
}

.chat-input-area textarea {
  flex: 1;
  border: none;
  background: transparent;
  padding: 8px 0;
  font-size: 16px;
  outline: none;
  font-family: inherit;
  color: #333;
  resize: none;
  overflow: hidden;
  min-height: 24px;
  max-height: 120px;
  line-height: 1.5;
}

.chat-input-area textarea::placeholder {
  color: #999;
}

.send-button {
  background: var(--theme-primary-color, #0F2FEB);
  border: none;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 50%;
  transition: all 0.2s ease;
  color: var(--theme-primary-text-color, #FFFFFF);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  min-height: 40px;
  margin-left: 8px;
}

.send-button:hover:not(:disabled) {
  background-color: var(--theme-secondary-color, #03187E);
  color: var(--theme-primary-text-color, #FFFFFF);
}

.send-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #e5e5e5;
  color: #999;
}

.send-button:focus {
  outline: 2px solid var(--theme-primary-color, #0F2FEB);
  outline-offset: 1px;
}

.send-button svg {
  display: block;
}


/* Responsive adjustments */
.ai-loading-indicator {
  display: flex;
  column-gap: 0.25rem;
}

.loading-blocks-svg-container {
  width: 1rem;
  height: 100%;
}

@media (max-width: 768px) {
  .chat-modal-overlay {
    padding: 0; /* Remove padding for full screen on mobile */
    z-index: 9999; /* Above navbar */
  }

  .chat-modal {
    width: 100%;
    height: 100vh; /* Fallback for older browsers */
    height: 100dvh; /* Full viewport height */
    max-width: 100%;
    max-height: 100vh;
    max-height: 100dvh;
    border-radius: 0;
    /* Full screen on small devices */
    bottom: 0;
    right: 0;
    position: relative;
  }

  .chat-button {
    padding: 12px 18px;
    bottom: 20px;
    right: 20px;
    font-size: 16px; /* Prevent zoom on iOS */
    min-height: 48px; /* Better touch target */
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  }

  .chat-modal-header {
    padding: calc(16px + env(safe-area-inset-top)) 20px 16px;
    min-height: 60px; /* Ensure adequate touch target height */
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: #f8f9fa;
  }

  .chat-input-area {
    padding: 16px 20px;
    position: sticky;
    bottom: 0;
    background-color: #f8f9fa;
    border-top: 2px solid #e9ecef;
    z-index: 10;
    padding-bottom: calc(16px + env(safe-area-inset-bottom));

  }

  .chat-input-area textarea {
    font-size: 16px; /* Prevent zoom on iOS */
    padding: 12px 0;
    min-height: 24px; /* Better touch target */
    max-height: 120px; /* Keep the same max-height on mobile */
  }

  .send-button {
    min-height: 48px; /* Better touch target */
    min-width: 48px;
    padding: 0 16px;
    font-size: 16px;
    border-radius: 24px;
  }

  .chat-messages {
    padding: 20px;
    padding-bottom: 40px; /* Extra space at bottom for better scrolling */
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
  }

  .message {
    max-width: 85%;
    margin-bottom: 16px;
  }

  .message-content {
    padding: 12px 16px;
    font-size: 16px; /* Prevent zoom and improve readability */
    line-height: 1.5;
  }

  /* Improve button spacing in header */
  .header-actions {
    gap: 12px;
  }

  .chatbot-reset-btn,
  .chatbot-close-btn {
    min-height: 44px; /* Better touch targets */
    min-width: 44px;
    padding: 8px 12px;
  }

  /* Reset confirmation banner mobile adjustments */
  .reset-confirmation-banner {
    padding: 16px 20px;
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .reset-warning-actions {
    justify-content: flex-end;
    gap: 12px;
  }

  .reset-cancel-btn,
  .reset-confirm-btn {
    min-height: 44px;
    padding: 8px 16px;
    font-size: 16px;
  }

  /* Mobile keyboard visibility adjustments */
  .chat-modal.keyboard-visible {
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
  }

  .chat-modal.keyboard-visible .chat-messages {
    padding-bottom: 20px; /* Reduce bottom padding when keyboard is visible */
  }
}



@media (max-width: 480px) {
  .chat-modal-header {
    padding: calc(16px + env(safe-area-inset-top)) 20px 16px;
    min-height: 56px;
  }

  .chat-modal-title {
    font-size: 1.1rem;
  }

  .chat-input-area {
    padding: 12px 16px;
  }

  .chat-input-area textarea {
    padding: 10px 14px;
    min-height: 44px;
    border-radius: 22px;
  }

  .chat-messages {
    padding: 16px;
    padding-bottom: 32px;
  }

  .message {
    max-width: 90%;
    margin-bottom: 12px;
  }

  .message-content {
    padding: 10px 14px;
    font-size: 15px;
  }

  .send-button span {
    display: none;
    /* Hide "Send" text, only show icon */
  }

  .send-button svg {
    margin-right: 0;
  }

  .send-button {
    padding: 0 12px;
    min-width: 44px;
    border-radius: 22px;
  }

  .header-actions {
    gap: 8px;
  }

  .chatbot-reset-btn {
    padding: 6px 10px;
    font-size: 0.75rem;
  }

  .chatbot-reset-btn span {
    display: none; /* Hide text on very small screens */
  }

  .chatbot-close-btn {
    min-width: 40px;
    min-height: 40px;
    padding: 6px;
  }

  /* Reset confirmation adjustments for small screens */
  .reset-confirmation-banner {
    padding: 12px 16px;
  }

  .reset-warning-title {
    font-size: 0.85rem;
  }

  .reset-warning-subtitle {
    font-size: 0.75rem;
  }

  .reset-cancel-btn,
  .reset-confirm-btn {
    min-height: 40px;
    padding: 6px 12px;
    font-size: 14px;
  }
}
</style>